import { Variants } from '@common/types';
import { Box, Button, ConfirmModal, Tooltip, TooltipPosition } from '@base-app';
import { FC, useState } from 'react';
import { OnSaveFormValues } from '../AddTeamMemberForm/AddTeamMemberForm.types';
import AddTeamMemberForm from '../AddTeamMemberForm/AddTeamMemberForm';
import {
  ErrorIconStyled,
  InfoIconStyled,
  SuccessIconStyled,
  TeamUserActionsCellStyled,
  TeamUserDataCellStyled,
  TeamUserDataCenteredCellStyled,
  TeamUserDataRoleCellStyled,
  TeamUserStyled,
} from './AddTeamMemberRow.styles';
import { useTranslation } from 'react-i18next';
import { AddTeamMemberRowProps } from './AddTeamMemberRow.types';
import { useModal } from '@components/hooks';

const AddTeamMemberRow: FC<AddTeamMemberRowProps> = ({
  onDelete,
  onEdit,
  onSave,
  user,
  disabled,
  sentInviteError,
  sentInviteResponse,
}) => {
  const { t } = useTranslation(['common', 'teams']);
  const [isEditMode, setIsEditMode] = useState(false);
  const { isShowing, toggle } = useModal();

  const handleEditClick = (): void => {
    setIsEditMode(true);
    onEdit(true);
  };

  const handleDeleteClick = (): void => {
    toggle();
  };

  const handleSaveForm = (values: OnSaveFormValues): void => {
    onSave(values);
    setIsEditMode(false);
    onEdit(false);
  };

  const handleCancelForm = (): void => {
    setIsEditMode(false);
    onEdit(false);
  };

  const handleConfirmDeletion = (): void => {
    toggle();
    onDelete();
  };

  let statusIcon = null;
  let rowCss = {};
  let cellCss;
  let statusMessage = '';

  if (disabled === true) {
    rowCss = { opacity: '0.3' };
  }

  if (sentInviteResponse !== undefined) {
    rowCss = { ...rowCss, color: '$darkGray200', fontWeight: '$normal' };

    const iconCss = { marginRight: '$7' };
    if (sentInviteError === true) {
      rowCss = { backgroundColor: '$red800', ...rowCss };
      statusIcon = <ErrorIconStyled css={iconCss} />;
      statusMessage = 'add_team_member_not_sent';
    } else {
      rowCss = { backgroundColor: '$green800', ...rowCss };
      statusIcon = <SuccessIconStyled css={iconCss} />;
      statusMessage = 'add_team_member_sent';
    }
    cellCss = { fontWeight: '$normal' };
  }

  const row = (
    <>
      <TeamUserStyled css={rowCss}>
        {sentInviteResponse !== undefined && (
          <TeamUserDataCellStyled css={{ ...cellCss, overflow: 'visible', display: 'flex', fontWeight: '$bold' }}>
            {statusIcon}
            <Box as="span" css={{ marginRight: '$2' }} data-testid="add-team-member-row-status">
              {t(statusMessage, { ns: 'teams' })}
            </Box>
            {sentInviteError === true && (
              <Tooltip text={sentInviteResponse} position={TooltipPosition.BOTTOM}>
                <InfoIconStyled css={{ verticalAlign: 'middle' }} />
              </Tooltip>
            )}
          </TeamUserDataCellStyled>
        )}
        <TeamUserDataCellStyled css={cellCss}>{`${user.firstName} ${user.lastName}`}</TeamUserDataCellStyled>
        <TeamUserDataCenteredCellStyled css={cellCss}>{user.email}</TeamUserDataCenteredCellStyled>
        <TeamUserDataRoleCellStyled css={cellCss}>{user.role.name}</TeamUserDataRoleCellStyled>
        {sentInviteResponse === undefined && (
          <TeamUserActionsCellStyled>
            <Button
              css={{ marginRight: '$2' }}
              data-testid="add-team-member-row-button-edit"
              disabled={disabled}
              variant={Variants.TERTIARY}
              onClick={handleEditClick}
            >
              {t('edit')}
            </Button>{' '}
            {'|'}
            <Button
              css={{ marginLeft: '$2' }}
              data-testid="add-team-member-row-button-delete"
              disabled={disabled}
              variant={Variants.TERTIARY}
              onClick={handleDeleteClick}
            >
              {t('delete')}
            </Button>
          </TeamUserActionsCellStyled>
        )}
      </TeamUserStyled>
      {isShowing && (
        <ConfirmModal
          cancelAction={{
            text: t('delete_modal_cancel'),
            onClick: toggle,
          }}
          confirmAction={{
            text: t('delete_modal_ok'),
            onClick: handleConfirmDeletion,
          }}
          description={t('delete_modal_text', { ns: 'teams' })}
          show={isShowing}
          title={t('delete_modal_title', { ns: 'teams' })}
        />
      )}
    </>
  );

  const form = (
    <TeamUserStyled
      css={{
        padding: '$8',
      }}
    >
      <AddTeamMemberForm onSaveForm={handleSaveForm} onCancelForm={handleCancelForm} user={user} />
    </TeamUserStyled>
  );

  return isEditMode ? form : row;
};

export default AddTeamMemberRow;
